import { ComponentType, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { ThemeProvider } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import LogoutIcon from "@mui/icons-material/Logout";
import MenuIcon from "@mui/icons-material/Menu";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import { useAuth } from "../../auth/AuthProvider";
import { buildMenuSections } from "../../auth/menu";
import { badgeLabel, initials } from "../../auth/permissions";
import AppearanceDialog from "../../features/preferences/AppearanceDialog";
import { usePreferences } from "../../features/preferences/PreferencesProvider";
import { MenuStyle } from "../../features/preferences/types";
import { useFeatures } from "../../hooks/useFeatures";
import Logo from "./brand/Logo";
import { useNotify } from "./feedback/SnackbarProvider";
import { BRAND, panelTheme } from "./theme";

export const RAIL_WIDTH = 64;
export const OPEN_WIDTH = 272;
export const TOPBAR_HEIGHT = 64;
export const MOBILE_QUERY = "(max-width:799.95px)";
// ids para aria-controls (há um só AppShell por página).
const NAV_ID = "app-shell-nav";
const DRAWER_NAV_ID = "app-shell-drawer-nav";
const USER_MENU_ID = "app-shell-user-menu";

type MenuItemDef = { name: string; route: string; icon: ComponentType<{ fontSize?: "small" | "inherit" | "medium" }> };
export type MenuSection = { title: string; items: MenuItemDef[] };

const NAV = {
  navy: { bg: BRAND.navy, fg: "rgba(255,255,255,.80)", icon: "rgba(255,255,255,.72)", head: "rgba(255,255,255,.48)", hover: "rgba(255,255,255,.07)", activeBg: "rgba(5,187,229,.18)", activeFg: "#fff", activeIcon: BRAND.cyan, border: "none", divider: "rgba(255,255,255,.12)" },
  light: { bg: "#ffffff", fg: "#374151", icon: BRAND.muted, head: "#9ca3af", hover: "#f3f4f6", activeBg: "#dff6fc", activeFg: BRAND.navy, activeIcon: BRAND.cyanDark, border: `1px solid ${BRAND.divider}`, divider: "#eceef1" },
} as const;

const isActive = (pathname: string, route: string) => pathname === route || pathname.startsWith(`${route}/`);

export function findCrumbs(sections: MenuSection[], pathname: string): string[] {
  for (const section of sections) {
    const item = section.items.find((i) => isActive(pathname, i.route));
    if (item) return [section.title, item.name];
  }
  return [];
}

type NavProps = { sections: MenuSection[]; pathname: string; collapsed: boolean; style: MenuStyle; onNavigate: (route: string) => void };

function NavItems({ sections, pathname, collapsed, style, onNavigate }: NavProps) {
  const c = NAV[style];
  return (
    <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", py: 1 }}>
      {sections.map((section, index) => (
        <Box key={section.title}>
          {collapsed && index > 0 && <Divider sx={{ borderColor: c.divider, mx: 1.5, my: 1 }} />}
          <List
            dense
            disablePadding
            aria-label={section.title}
            subheader={
              collapsed ? undefined : (
                <ListSubheader disableSticky sx={{ bgcolor: "transparent", color: c.head, fontSize: 11, fontWeight: 600, letterSpacing: 1, textTransform: "uppercase", lineHeight: "28px", pt: index ? 1 : 0, px: 2.5 }}>
                  {section.title}
                </ListSubheader>
              )
            }
          >
            {section.items.map((item) => {
              const active = isActive(pathname, item.route);
              const Icon = item.icon;
              const button = (
                <ListItemButton
                  key={item.route}
                  aria-current={active ? "page" : undefined}
                  aria-label={collapsed ? item.name : undefined}
                  onClick={() => onNavigate(item.route)}
                  sx={{
                    position: "relative",
                    height: 42,
                    mx: collapsed ? 1 : 1.25,
                    my: "2px",
                    px: collapsed ? 0 : 1.5,
                    justifyContent: collapsed ? "center" : "flex-start",
                    borderRadius: style === "light" ? "999px" : "8px",
                    color: active ? c.activeFg : c.fg,
                    bgcolor: active ? c.activeBg : "transparent",
                    "&:hover": { bgcolor: active ? c.activeBg : c.hover },
                    "&::before": style === "navy" && active ? { content: '""', position: "absolute", left: collapsed ? -8 : -10, top: 8, bottom: 8, width: 3, borderRadius: 3, bgcolor: BRAND.cyan } : {},
                  }}
                >
                  <ListItemIcon sx={{ minWidth: collapsed ? 0 : 36, color: active ? c.activeIcon : c.icon, justifyContent: "center" }}>
                    <Icon fontSize="small" />
                  </ListItemIcon>
                  {!collapsed && <ListItemText primary={item.name} primaryTypographyProps={{ fontSize: 13, fontWeight: active ? 600 : 500, noWrap: true }} />}
                </ListItemButton>
              );
              return collapsed ? (
                <Tooltip key={item.route} title={item.name} placement="right">
                  {button}
                </Tooltip>
              ) : (
                button
              );
            })}
          </List>
        </Box>
      ))}
    </Box>
  );
}

export default function AppShell({ Page }: { Page: ComponentType }) {
  const { me, logout } = useAuth();
  const { features } = useFeatures();
  const { prefs, setPref } = usePreferences();
  const notify = useNotify();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const mobile = useMediaQuery(MOBILE_QUERY);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenu, setUserMenu] = useState<HTMLElement | null>(null);
  const [appearanceOpen, setAppearanceOpen] = useState(false);
  const sections = buildMenuSections(me, features) as MenuSection[];
  const crumbs = findCrumbs(sections, pathname);
  const badge = badgeLabel(me);
  const style: MenuStyle = prefs.menuStyle;
  const c = NAV[style];
  const collapsed = prefs.menuCollapsed;
  const logoTone = style === "navy" ? "negative" : "positive";

  const go = (route: string) => {
    setMobileOpen(false);
    navigate(route);
  };
  const onLogout = () => {
    setUserMenu(null);
    logout().catch(() => notify({ severity: "error", message: "Não foi possível sair. Tente de novo." }));
  };

  const rail = (
    <Box
      component="nav"
      id={NAV_ID}
      aria-label="Menu principal"
      data-menu-style={style}
      sx={{
        width: collapsed ? RAIL_WIDTH : OPEN_WIDTH,
        minWidth: collapsed ? RAIL_WIDTH : OPEN_WIDTH,
        transition: (t) => t.transitions.create(["width", "min-width"], { duration: 300 }),
        bgcolor: c.bg,
        borderRight: c.border,
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
      }}
    >
      {/* Igual ao ME web (Sidebar.vue): recolhido = só a seta › centralizada; aberto = logo + seta ‹ (D-OR2). */}
      <Box sx={{ height: TOPBAR_HEIGHT, minHeight: TOPBAR_HEIGHT, display: "flex", alignItems: "center", justifyContent: collapsed ? "center" : "space-between", px: collapsed ? 0 : 2, borderBottom: `1px solid ${c.divider}` }}>
        {!collapsed && <Logo variant="horizontal" tone={logoTone} height={30} />}
        <Tooltip title={collapsed ? "Expandir menu" : "Recolher menu"} placement="right">
          <IconButton
            aria-label={collapsed ? "Expandir menu" : "Recolher menu"}
            aria-expanded={!collapsed}
            aria-controls={NAV_ID}
            onClick={() => setPref("menuCollapsed", !collapsed)}
            sx={{ color: c.icon, width: 36, height: 36, borderRadius: "8px", "&:hover": { bgcolor: c.hover } }}
          >
            {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </Tooltip>
      </Box>
      <NavItems sections={sections} pathname={pathname} collapsed={collapsed} style={style} onNavigate={go} />
    </Box>
  );

  return (
    <ThemeProvider theme={panelTheme}>
      <Box sx={{ display: "flex", height: "100vh", "@supports (height: 100dvh)": { height: "100dvh" }, bgcolor: "background.default" }}>
        {!mobile && rail}
        <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
          <Box component="header" sx={{ height: TOPBAR_HEIGHT, minHeight: TOPBAR_HEIGHT, bgcolor: "#fff", borderBottom: `1px solid ${BRAND.divider}`, display: "flex", alignItems: "center", gap: 1.5, px: { xs: 1, sm: 2.5 } }}>
            {mobile && (
              <>
                <IconButton aria-label="Abrir menu" aria-expanded={mobileOpen} aria-controls={DRAWER_NAV_ID} onClick={() => setMobileOpen(true)}>
                  <MenuIcon />
                </IconButton>
                <Logo variant="symbol" height={24} />
              </>
            )}
            <Typography aria-label="Você está em" sx={{ fontSize: 13, color: "text.secondary", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>
              {crumbs.map((crumb, i) => (
                <span key={crumb}>
                  {i > 0 && <span style={{ margin: "0 6px", color: "#c0c6cf" }}>/</span>}
                  <span style={{ color: i === crumbs.length - 1 ? BRAND.navy : undefined, fontWeight: i === crumbs.length - 1 ? 600 : 400 }}>{crumb}</span>
                </span>
              ))}
            </Typography>
            <Box sx={{ flex: 1 }} />
            <ButtonBase
              aria-label="Menu do usuário"
              aria-haspopup="menu"
              aria-expanded={Boolean(userMenu)}
              aria-controls={userMenu ? USER_MENU_ID : undefined}
              onClick={(e) => setUserMenu(e.currentTarget)} sx={{ display: "flex", alignItems: "center", gap: 1, borderRadius: 2, px: 0.5, py: 0.5, minHeight: 40 }}>
              <Avatar src={me?.avatarUrl || undefined} alt="" sx={{ bgcolor: BRAND.cyan, width: 32, height: 32, fontSize: 13, fontWeight: 700 }}>
                {initials(me?.name, me?.email)}
              </Avatar>
              {!mobile && (
                <Box sx={{ lineHeight: 1.1, textAlign: "left" }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{me?.name || me?.email}</Typography>
                  {badge && <Typography sx={{ fontSize: 11, color: "text.secondary" }}>{badge}</Typography>}
                </Box>
              )}
            </ButtonBase>
            <Menu MenuListProps={{ id: USER_MENU_ID }} anchorEl={userMenu} open={Boolean(userMenu)} onClose={() => setUserMenu(null)} anchorOrigin={{ vertical: "bottom", horizontal: "right" }} transformOrigin={{ vertical: "top", horizontal: "right" }}>
              <MenuItem onClick={() => { setUserMenu(null); setAppearanceOpen(true); }}>
                <ListItemIcon><PaletteOutlinedIcon fontSize="small" /></ListItemIcon>
                Aparência
              </MenuItem>
              <MenuItem onClick={onLogout}>
                <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
                Sair
              </MenuItem>
            </Menu>
          </Box>
          <Box component="main" sx={{ flex: 1, minHeight: 0, minWidth: 0, overflow: "auto" }}>
            <Page />
          </Box>
        </Box>
        {mobile && (
          <Drawer variant="temporary" open={mobileOpen} onClose={() => setMobileOpen(false)} PaperProps={{ sx: { width: OPEN_WIDTH, maxWidth: "85vw", bgcolor: c.bg } }}>
            <Box component="nav" id={DRAWER_NAV_ID} aria-label="Menu principal" data-menu-style={style} sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
              <Box sx={{ height: TOPBAR_HEIGHT, display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, borderBottom: `1px solid ${c.divider}` }}>
                <Logo variant="horizontal" tone={logoTone} height={28} />
                <IconButton aria-label="Fechar menu" onClick={() => setMobileOpen(false)} sx={{ color: c.icon }}>
                  <CloseIcon />
                </IconButton>
              </Box>
              <NavItems sections={sections} pathname={pathname} collapsed={false} style={style} onNavigate={go} />
            </Box>
          </Drawer>
        )}
        <AppearanceDialog open={appearanceOpen} onClose={() => setAppearanceOpen(false)} />
      </Box>
    </ThemeProvider>
  );
}
